import { EnhancedBaseService } from '../base/EnhancedBaseService';

import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';

export interface CampaignDocument {
  id: string;
  campaign_id: string;
  user_id: string;
  file_name: string;
  storage_path: string;
  mime_type: string;
  size_bytes: number;
  extracted_text: string | null;
  extraction_status: 'pending' | 'ok' | 'failed' | 'skipped';
  /** Plain-English reason the document could not be read. Shown verbatim. */
  extraction_error: string | null;
  created_at: string;
}

const BUCKET_ID = 'plan-documents';

/** `text` set means it was read; otherwise `message` says why it could not be. */
interface ExtractionAttempt {
  text: string | null;
  message: string | null;
}

class CampaignDocumentService extends EnhancedBaseService {
  async listForCampaign(campaignId: string): Promise<CampaignDocument[]> {
    const { data, error } = await this.client
      .from('campaign_documents')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data as unknown as CampaignDocument[]) ?? [];
  }

  async upload(campaignId: string, file: File): Promise<CampaignDocument> {
    const {
      data: { user },
    } = await this.client.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Shared-account RLS: campaign_documents rows must be owned by
    // current_account_owner_id(), not the raw session user — otherwise a
    // team member's upload violates the INSERT check and fails.
    const ownerId = await resolveOwnerId(user.id);

    const sanitized = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-160);
    const id = crypto.randomUUID();
    const path = `${user.id}/${campaignId}/${id}-${sanitized}`;

    const { error: uploadError } = await this.client.storage.from(BUCKET_ID).upload(path, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type,
    });
    if (uploadError) throw uploadError;

    let extractedText: string | null = null;
    let extractionStatus: CampaignDocument['extraction_status'] = 'skipped';
    let extractionError: string | null = null;

    if (this.isTextMime(file.type)) {
      try {
        const raw = await file.text();
        const cleaned =
          file.type === 'text/html'
            ? raw
                .replace(/<script[\s\S]*?<\/script>/gi, ' ')
                .replace(/<style[\s\S]*?<\/style>/gi, ' ')
                .replace(/<[^>]+>/g, ' ')
                .replace(/&nbsp;/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
            : raw;
        extractedText = cleaned.slice(0, 100_000);
        extractionStatus = 'ok';
      } catch {
        extractionStatus = 'failed';
        extractionError = 'This file could not be read.';
      }
    } else if (this.isServerExtractableMime(file.type)) {
      // PDFs used to be stored as 'pending' here and NOTHING advanced them, so
      // the document sat in the list looking fine while the AI could not see a
      // word of it. Extraction now happens inline and its outcome — including
      // "this is a scan, there is no text" — is recorded and shown.
      const { text, message } = await this.extractOnServer(file);
      if (text) {
        extractedText = text.slice(0, 100_000);
        extractionStatus = 'ok';
      } else {
        extractionStatus = 'failed';
        extractionError = message ?? 'This document could not be read.';
      }
    } else {
      extractionStatus = 'skipped';
    }

    const { data, error } = await this.client
      .from('campaign_documents')
      .insert({
        id,
        campaign_id: campaignId,
        user_id: ownerId,
        file_name: file.name,
        storage_path: path,
        mime_type: file.type,
        size_bytes: file.size,
        extracted_text: extractedText,
        extraction_status: extractionStatus,
        extraction_error: extractionError,
      })
      .select('*')
      .single();
    if (error) {
      // Best-effort cleanup of orphaned blob
      await this.client.storage
        .from(BUCKET_ID)
        .remove([path])
        .catch(() => undefined);
      throw error;
    }
    return data as unknown as CampaignDocument;
  }

  private isTextMime(mime: string): boolean {
    return mime === 'text/plain' || mime === 'text/markdown' || mime === 'text/html';
  }

  private isServerExtractableMime(mime: string): boolean {
    return (
      mime === 'application/pdf' ||
      mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    );
  }

  /**
   * PDF/DOCX parsing lives on the server (`unpdf`/`mammoth` must never reach a
   * browser bundle). A failure returns the server's own sentence rather than a
   * generic one, because that sentence is what the user is shown.
   */
  private async extractOnServer(file: File): Promise<ExtractionAttempt> {
    const generic = 'This document could not be read.';
    try {
      const {
        data: { session },
      } = await this.client.auth.getSession();

      const response = await fetch('/api/documents/extract-text', {
        method: 'POST',
        headers: {
          'Content-Type': file.type,
          'x-file-name': file.name,
          ...(session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : undefined),
        },
        body: file,
      });

      const payload = (await response.json().catch(() => null)) as {
        text?: string;
        error?: string;
      } | null;

      if (!response.ok) {
        return { text: null, message: payload?.error?.trim() || generic };
      }

      const text = payload?.text?.trim() ?? '';
      if (!text) return { text: null, message: generic };
      return { text, message: null };
    } catch {
      return {
        text: null,
        message: 'This document could not be read — the extraction service did not respond.',
      };
    }
  }

  async remove(id: string): Promise<void> {
    const { data: doc, error: fetchError } = await this.client
      .from('campaign_documents')
      .select('storage_path')
      .eq('id', id)
      .maybeSingle();
    if (fetchError) throw fetchError;

    if (doc?.storage_path) {
      await this.client.storage
        .from(BUCKET_ID)
        .remove([doc.storage_path])
        .catch(() => undefined);
    }
    const { error } = await this.client.from('campaign_documents').delete().eq('id', id);
    if (error) throw error;
  }

  async signedDownloadUrl(path: string, ttlSeconds = 300): Promise<string | null> {
    const { data, error } = await this.client.storage
      .from(BUCKET_ID)
      .createSignedUrl(path, ttlSeconds);
    if (error || !data) return null;
    return data.signedUrl;
  }
}

export const campaignDocumentService = new CampaignDocumentService();
